// @ts-strict-ignore

import { BehaviorSubject } from "rxjs/internal/BehaviorSubject";
import { Subject, Subscription } from "rxjs";
import { escape } from 'rainbow-web-sdk/libs';
import { FileViewer } from 'rainbow-web-sdk/models/common/fileViewer';
import { isBefore } from 'date-fns';
import { Contact } from "../contact.model";

/**
 * @enum
 * @name FileStorageType
 * @description This enum is used to represent the type of the storage (Rainbow or External)
 */
export enum FileStorageType {
    RAINBOW = 'Rainbow',
    OWNCLOUD = 'OwnCloud',
    NEXTCLOUD = 'NextCloud'
}

export enum FileState {
    DELETED = "deleted",
    UPLOADING = "uploading",
    UPLOADED = "uploaded",
    NOT_UPLOADED = "not_uploaded",
    DOWNLOADING = "downloading",
    UPLOADERROR = "uploadError",
    UNKNOWN = "unknown"
}

class ThumbnailPlaceholder {
    constructor(public icon: string, public style: string, public type: string) { }
}

interface IThumbnail {
    availableThumbnail: boolean;
    md5sum: string;
    size: number;
    wantThumbnailDate: Date;
    isThumbnailAvailable(): boolean;
}

class Thumbnail implements IThumbnail {
    availableThumbnail: boolean;
    md5sum: string;
    size: number;
    wantThumbnailDate: Date;

    constructor(data: any) {
        if (data) {
            this.availableThumbnail = data.availableThumbnail;
            this.md5sum = data.md5sum;
            this.size = data.size;
            this.wantThumbnailDate = data.wantThumbnailDate;
        } else {
            this.availableThumbnail = false;
        }
    }

    public isThumbnailAvailable(): boolean {
        return this.availableThumbnail;
    }
}

/**
 * This class is used to represent a File Descriptor or a Short File Descriptor which describes a file shared in a conversation (one-to-one or bubble).
 */
export class FileDescriptor {

    /**
     * @public
     * @property {string} id The file descriptor ID (File Descriptor only)
     * @readonly
     */
    public id: string;

    /**
     * @public
     * @property {string} path The file descriptor path
     * @readonly
     */
    public path: string = '';

    /**
     * @public
     * @property {string} type The file descriptor type (file or directory)
     * @readonly
     */
    public type: string = 'file';

    /**
     * @public
     * @property {string} url The file descriptor url (File Descriptor only)
     * @readonly
     */
    public url: string = null;

    /**
     * @public
     * @property {string} ownerId The ID of the owner (File Descriptor only)
     * @readonly
     */
    public ownerId: string = null;

    /**
     * @public
     * @property {string} owner The owner's contact (File Descriptor only)
     * @readonly
     */
    public owner: Contact;

    /**
     * @public
     * @property {string} fileName The name of the file
     * @readonly
     */
    public fileName: string = null;

    /**
     * @public
     * @property {string} fileNameToFilter The name of the file avfter dyacritis
     * @readonly
     */
    public fileNameToFilter: string;

    /**
     * @public
     * @property {string} extension The extension of the file
     * @readonly
     */
    public extension: string;

    /**
     * @public
     * @property {string} typeMIME The mime type of the file ('mime' in Short File Descriptor)
     * @readonly
     */
    public typeMIME: string;

    /**
     * @public
     * @property {string} size The size of the file (octets)
     * @readonly
     */
    public size: number;

    /**
     * @public
     * @property {object} registrationDate The creation date (File Descriptor only)
     * @readonly
     */
    public registrationDate: Date;

    /**
     * @public
     * @property {object} uploadedDate The upload date (File Descriptor only)
     * @readonly
     */
    public uploadedDate: Date;

    /**
     * @private
     * @property {object} dateToSort The date to sort (?)
     * @readonly
     */
    public dateToSort: Date;

    /**
     * @private
     * @property {boolean} externalStorage True if this fileDescriptor is linked to external storage
     * @readonly
     */
    public externalStorage: boolean = false;

    /**
     * @public
     * @property {string} transcriptionStatus the state of transcription
     * @readonly
     */
    public transcriptionStatus: string = null;

    /**
     * @public
     * @property {string} transcription the text of the transcription
     * @readonly
     */
    public transcription: string = null;

    /**
     * @public
     * @property {boolean} transcriptionAlreadyActivated To know if the transcription has been launched
     * @readonly
     */
    public transcriptionAlreadyActivated: boolean = false;

    /**
     * @private
     * @property {any[]} viewers The list of viewers (File Descriptor only)
     * @readonly
     */
    private _viewers: FileViewer[];
    public get viewers(): FileViewer[] { return this._viewers; }
    public set viewers(value: FileViewer[]) { this._viewers = value; this.viewersSubject.next(this._viewers); }
    public viewersSubject = new BehaviorSubject<FileViewer[]>([]);

    public state: FileState;
    public stateSubject: BehaviorSubject<FileState>;
    public fileToSend?: File = undefined;
    public previewBlob?: Blob = undefined;
    public previewBlobSubject: BehaviorSubject<Blob> = new BehaviorSubject<Blob>(undefined);
    public blobOrientation?: any = undefined;
    public blobOrientationSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);

    /**
     * @public
     * @property {number} original_h original image height
     * @description height of original image (for image only)
     */
    public original_h: number = 0;

    /**
     * @public
     * @property {number} original_w original image width
     * @description weight of original image (for image only)
     */
    public original_w: number = 0;

    /**
     * @private
     * @property {object} tags data for visual voice mails
     * @readonly
     */
    public tags: any = null;

    public time: number;
    public displayDate: any;

    /**
     * @public
     * @property {Subject<any>} progressSubject Observable for download progress
     * @readonly
     */
    public progressSubject: Subject<any> = new Subject<any>();

    /**
     * @public
     * @property {any} localURL ????
     * @readonly
     */
    public localURL: any;

    // Download information Part :
    public cancelTransfertDefer: any; // TODO: DELETE
    public abortTransferController: AbortController = new AbortController();
    public abortXMLHttpRequestArray: Function[] = [];
    public fileTransferSubscription: Subscription;
    public loadingRef: number[] = [];
    public chunkTotalNumber: number;
    public chunkPerformedPercent: number;
    public chunkPercentArray: number[];
    public chunkPerformed: number;

    // Temporary Token for Public Url
    public publicUrl: string;
    public publicUrlExpirationDate: Date;

    // For Desktop file chunk Written :
    public chunkWrittenInDisk: number;
    public writeStream: any;

    // Thumbnail:
    public thumbnail: IThumbnail;

    /**
     * @public
     * @property {ThumbnailPlaceholder} thumbnailPlaceholder The thumbnail icon placeholder info
     * @readonly
     */
    public thumbnailPlaceholder: ThumbnailPlaceholder;

    /**
     * @public
     * @property {number} orientation image orientation
     * @description
     *      There are four possible values for orientation and the image should be rotated acording to this value.
     *      1 -> rotate(0deg),
     *      3 -> rotate(180deg),
     *      6 -> rotate(90deg),
     *      8 -> rotate(270deg).
     */
    public orientation: number;

    /**
     * @public
     * @property {string} virusScanState Result of virus scan
     * @readonly
     */
    public virusScanState: string = null;

    /**
     * @public
     * @property {boolean} voiceMessage True when the file is a voice message
     * @readonly
     */
    public voiceMessage: boolean = false;

    /**
     * @public
     * @property {boolean} recordingFile Property indicating whether the file is a part of a recording file batch
     * @readonly
     */
    public recordingFile: boolean = false;

    /**
     * @public
     * @property {string} recordingConferenceId ID of the recording conference. If the file is a recording file it indicates the ID of the conference
     * @readonly
     */
    public recordingConferenceId: string = '';

    /**
     * @public
     * @property {boolean} voiceMessage True when the preview is downloaded
     * @readonly
     */
    public previewDownloaded: boolean = false;

    private constructor(id: string, fileName: string, mime: string, size: number, type?: string) {
        this.id = id;
        this.fileName = escape(fileName);
        this.fileNameToFilter = this.fileName.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        this.typeMIME = mime;
        if (type) { this.type = type; }
        this.thumbnailPlaceholder = this.getThumbnailPlaceholderFromMimetype(mime);
        this.size = size;
    }

    public static create(id: string, url: string, ownerId: string, fileName: string, extension: string, mime: string,
        size: number, registrationDate: Date, uploadedDate: Date, dateToSort: Date, viewers: any, state: FileState, thumbnail: IThumbnail,
        orientation: number, original_w: number, original_h: number, tags: any, virusScanState: any, voiceMessage: any, recordingFile: boolean, recordingConferenceId: string, transcriptionStatus: string, transcription: string): FileDescriptor {

        const fileDescriptor = new FileDescriptor(id, fileName, mime, size);
        fileDescriptor.url = url;
        fileDescriptor.ownerId = ownerId;
        fileDescriptor.extension = extension;
        fileDescriptor.registrationDate = registrationDate;
        fileDescriptor.uploadedDate = uploadedDate;
        fileDescriptor.dateToSort = dateToSort;
        fileDescriptor.viewers = viewers;
        fileDescriptor.state = state;
        fileDescriptor.stateSubject = new BehaviorSubject(fileDescriptor.state);
        fileDescriptor.thumbnail = new Thumbnail(thumbnail);
        fileDescriptor.orientation = orientation ? orientation : undefined;
        fileDescriptor.original_w = original_w ? original_w : 0;
        fileDescriptor.original_h = original_h ? original_h : 0;
        fileDescriptor.tags = tags;
        fileDescriptor.virusScanState = virusScanState;
        fileDescriptor.voiceMessage = voiceMessage;
        fileDescriptor.recordingFile = recordingFile;
        fileDescriptor.recordingConferenceId = recordingConferenceId;
        fileDescriptor.time = fileDescriptor.getDate().getTime();
        fileDescriptor.transcriptionStatus = transcriptionStatus;
        fileDescriptor.transcription = transcription;

        return fileDescriptor;
    }

    public static createFromExternalStorageData(data: any) {
        const fileDescriptor = new FileDescriptor(data.fileId, data.basename, data.mime, data.size, data.type);
        fileDescriptor.ownerId = data.ownerId;
        fileDescriptor.path = data.filename;
        fileDescriptor.dateToSort = new Date(data.lastmod);
        fileDescriptor.time = fileDescriptor.dateToSort.getTime();
        fileDescriptor.externalStorage = true;
        return fileDescriptor;
    }

    public isMicrosoftFile(): boolean {
        const mediaExtension: string[] = ["docx", "doc", "ppt", "pptx", "xls", "xlsx"];
        return (mediaExtension.some(ext => ext === this.extension));
    }

    public isRainbowStorage(): boolean { return !this.externalStorage; }

    public isThumbnailPossible(): boolean { return (this.isImage() || this.isPDF() || this.isVideo()); }

    public isJavaScript(): boolean { return (this.typeMIME === "application/javascript"); }

    public isText(): boolean { return (this.typeMIME === "text/plain"); }

    public isLog(): boolean { return (this.typeMIME === "text/x-log"); }

    public isMarkdown(): boolean { return (this.typeMIME === "text/markdown"); }

    public isPDF(): boolean { return (this.typeMIME === "application/pdf"); }

    public isVideo(): boolean { return (this.typeMIME?.indexOf("video/") !== -1); }

    public isSVG(): boolean { return this.extension === 'svg'; }

    public isImage(): boolean {
        const imgType = "image/";
        return this.typeMIME && this.typeMIME.length >= imgType.length && this.typeMIME.slice(0, imgType.length) === imgType;
    }

    public isAudioVideo(): boolean {
        const mediaExtension: string[] = ["avi", "mpg", "wma", "mp3", "mkv", "mov", "wav", "ogg", "mp4", "aac", "webm"];
        return (mediaExtension.some(ext => ext === this.extension));
    }

    public isUploaded(): boolean {
        return this.state && this.state === FileState.UPLOADED;
    }

    public isPublicUrlExpired(): boolean {
        if (!this.publicUrlExpirationDate) return false;

        const nowDate = new Date();
        return isBefore(nowDate, this.publicUrlExpirationDate);
    }

    public isAlreadyFileViewer(viewerId: string): boolean {
        if (this.viewers) return this.viewers.some(viewer => viewer.viewerId === viewerId);
        return false;
    }

    public getDisplayName(): string {
        return escape(this.fileName.replace(/\.[^/.]+$/, ""));
    }

    public getDisplayNameTruncated(): string[] {
        const str = this.fileName.replace(/\.[^/.]+$/, "");
        return [str.substring(0, str.length - 4), str.slice(-4)];
    }

    public getExtension(): string {
        if (this.fileName.toUpperCase() === this.extension.toUpperCase()) { return ""; }
        return "." + this.extension;
    }

    public getDuration(): number {
        if (this.tags?.duration) { return Math.round(this.tags.duration); }
        return 0;
    }

    public getThumbnailPlaceholderFromMimetype(mime: string): ThumbnailPlaceholder {
        if (this.type === 'directory') return new ThumbnailPlaceholder('folder-plain', 'folderStyle', 'other');
        if (!mime) {
            return new ThumbnailPlaceholder('unknown-type', 'otherStyle', 'other');
        }

        if (mime === "image/vnd.adobe.photoshop" || mime === "image/photoshop" || mime === "image/x-photoshop" || mime === "image/psd" || mime === "application/photoshop" || mime === "application/psd") {
            return new ThumbnailPlaceholder('psd-type', 'psdStyle', 'psd');
        }

        if (mime.indexOf("image") === 0) {
            return new ThumbnailPlaceholder('image-type', 'imageStyle', 'image');
        }

        if (mime === "application/msword"
            || ((/^application\/vnd.openxmlformats-officedocument.wordprocessingml.*$/).test(mime))
            || mime === "application/vnd.oasis.opendocument.text") {
            return new ThumbnailPlaceholder('document-type', 'docStyle', 'doc');
        }

        if ((/^application\/vnd.ms-powerpoint.*$/).test(mime)
            || ((/^application\/vnd.openxmlformats-officedocument.presentationml.*$/).test(mime))
            || mime === "application/vnd.oasis.opendocument.presentation") {
            return new ThumbnailPlaceholder('powerpoint-type', 'pptStyle', 'ppt');
        }

        if (mime.indexOf("application/vnd.ms-excel") === 0
            || ((/^application\/vnd.openxmlformats-officedocument.spreadsheetml.*$/).test(mime))) {
            return new ThumbnailPlaceholder('excel-type', 'xlsStyle', 'xls');
        }

        if (mime === "application/pdf" || mime === "application/vnd.oasis.opendocument.spreadsheet") {
            return new ThumbnailPlaceholder('pdf-type', 'pdfStyle', 'pdf');
        }

        if (mime.indexOf("video/") === 0) {
            return new ThumbnailPlaceholder('video-type', 'videoStyle', 'video');
        }

        if (mime.indexOf("audio/") === 0) {
            return new ThumbnailPlaceholder('audio-type', 'audioStyle', 'audio');
        }

        if (mime === "text/plain") {
            return new ThumbnailPlaceholder('txt-type', 'txtStyle', 'txt');
        }

        if (mime === "application/zip" || mime === "application/x-rar-compressed" || mime === "application/x-7z-compressed" || mime === "application/x-tar") {
            return new ThumbnailPlaceholder('archive-type', 'archiveStyle', 'archive');
        }

        if (mime === "application/illustrator" || mime.indexOf("application/postscript") === 0) {
            return new ThumbnailPlaceholder('ai-type', 'aiStyle', 'ai');
        }

        if (mime === "text/css" || mime === "text/html" || mime === "application/javascript" || mime === "application/json" || mime === "application/typescript" || mime === "application/xml") {
            return new ThumbnailPlaceholder('code-type', 'codeStyle', 'code');
        }

        return new ThumbnailPlaceholder('unknown-type', 'otherStyle', 'other');
    }

    public releaseURL() {
        this.localURL = undefined;
    }

    public setState(newState: FileState) {
        this.state = newState;
        this.stateSubject.next(this.state);
    }

    public setOriginalImageDimensions(width: number, height: number) {
        this.original_w = width;
        this.original_h = height;
    }

    public update(fd: FileDescriptor) {
        this.id = fd.id;
        this.url = fd.url;
        this.ownerId = fd.ownerId;
        this.fileName = escape(fd.fileName);
        this.extension = fd.extension;
        this.typeMIME = fd.typeMIME;
        this.size = fd.size;
        this.registrationDate = fd.registrationDate;
        this.uploadedDate = fd.uploadedDate;
        this.dateToSort = fd.dateToSort;
        this.viewers = fd.viewers;
        this.setState(fd.state);
        this.thumbnail = fd.thumbnail;
        this.orientation = fd.orientation;
        this.time = this.getDate().getTime();
        this.transcriptionStatus = fd.transcriptionStatus;
        this.transcription = fd.transcription;
    }

    public getDate(): Date {
        if (this.uploadedDate) { return new Date(this.uploadedDate); }
        if (this.registrationDate) { return new Date(this.registrationDate); }
        return new Date(this.dateToSort);
    }

    private refreshFilePercent(): void {
        let chunkPercentTotal = 0;
        this.chunkPercentArray.forEach((chunkPercent) => {
            chunkPercentTotal += chunkPercent;
        });
        //console.log("[FileServerService] refreshFilePercent:  chunkPercentTotal=" + chunkPercentTotal);
        this.chunkPerformedPercent = Math.floor(chunkPercentTotal / this.chunkTotalNumber);
        //console.log("[FileServerService] refreshFilePercent:  chunkPerformedPercent=" + this.chunkPerformedPercent);
    }

    public updateProgressInfo() {
        this.refreshFilePercent();
        this.progressSubject.next({});
    }

    public isInfected() {
        return this.virusScanState === "isInfected";
    }

    public isSoleFileViewer(fileViewerId: string): boolean {
        if (this.viewers) {
            return this.viewers.length === 1 && this.viewers[0].viewerId === fileViewerId;
        }
        return false;
    }
}
